import type { Exercise } from '../../types/exercise'

/**
 * A protocol and the rows that are the same protocol at a different load.
 *
 * Variants are separate exercises on purpose — someone working Max Hangs at
 * 90% across a season needs that to stay its own comparable series — but nine
 * near-identical rows in a picker is not a list anyone can read. This is where
 * they go back together.
 */
export interface ExerciseFamily {
  /** Stable key for React and for expansion state. */
  key: string
  /** What the card is called: the family name, or the title of a lone exercise. */
  name: string
  /** The row the card borrows its image, category and duration from. */
  lead: Exercise
  variants: Exercise[]
}

/**
 * Groups a result list by family, preserving the order the server sent.
 *
 * An exercise with no family is a family of one, keyed by its id so two
 * user-created exercises that happen to share a title stay apart. Grouping on
 * the translated family name is safe because one response is always in one
 * locale.
 */
export function groupExercisesByFamily(exercises: Exercise[]): ExerciseFamily[] {
  const families: ExerciseFamily[] = []
  const byKey = new Map<string, ExerciseFamily>()

  for (const exercise of exercises) {
    const key = exercise.family ? `family:${exercise.family}` : `exercise:${exercise.id}`
    const existing = byKey.get(key)

    if (existing) {
      existing.variants.push(exercise)
      continue
    }

    const family: ExerciseFamily = {
      key,
      name: exercise.family ?? exercise.title,
      lead: exercise,
      variants: [exercise],
    }
    byKey.set(key, family)
    families.push(family)
  }

  return families
}

/** A family of one renders as a plain row: there is nothing to expand into. */
export function isSingleton(family: ExerciseFamily): boolean {
  return family.variants.length === 1
}

/** What a variant is called inside its card, falling back to the full title. */
export function variantLabel(exercise: Exercise): string {
  return exercise.variant_label ?? exercise.title
}
